import * as React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINTS, DARK_COLORS } from '@/constants';

import { BaseWrapper as AsideWrapper } from '@/components/Aside';
import InPortal from '@/components/InPortal';
import Space from '@/components/Space';
import FullWidthChild from '@/components/ContentGrids/FullWidthChild';

function CodeWrapper({
  stacked,
  size,
  className,
  children,
  isFullscreened,
}) {
  const inlineRef = React.useRef(null);

  const [isActuallyFullscreened, setIsActuallyFullscreened] =
    React.useState(isFullscreened);
  const [blockerSize, setBlockerSize] = React.useState(0);

  React.useEffect(() => {
    setIsActuallyFullscreened(isFullscreened);

    if (!inlineRef.current) {
      return;
    }

    if (isFullscreened) {
      const boundingBox = inlineRef.current.getBoundingClientRect();

      setBlockerSize(boundingBox.height);
    } else {
      setBlockerSize(0);
    }
  }, [isFullscreened]);

  // NOTE: Not currently supported.
  if (isActuallyFullscreened) {
    return (
      <>
        <InlineOuterWrapper>
          <InlineWrapper
            style={{ ...DARK_COLORS, height: blockerSize }}
          />
        </InlineOuterWrapper>
        <InPortal>
          <FullscreenWrapper style={DARK_COLORS}>
            {children}
          </FullscreenWrapper>
        </InPortal>
      </>
    );
  }

  return (
    <InlineOuterWrapper
      data-stacked={String(!!stacked)}
      as={size === 'wide' ? FullWidthChild : 'div'}
    >
      <InlineWrapper
        style={DARK_COLORS}
        className={className}
        ref={inlineRef}
      >
        {children}
      </InlineWrapper>
    </InlineOuterWrapper>
  );
}

const InlineOuterWrapper = styled.div`
  margin-bottom: 48px;

  @media ${BREAKPOINTS.mdAndSmaller} {
    margin-bottom: 32px;
  }

  &[data-stacked='true'] {
    margin-bottom: 16px;
  }
`;

export const InlineWrapper = styled(Space)`
  --selection-background-color: hsl(250deg 20% 60% / 0.35);
  --playground-corner-radius: 6px;
  --encroach-by-left: 16px;
  --encroach-by-right: 16px;
  --color-primary: hsl(50deg 100% 50%) !important;

  position: relative;
  z-index: 1;
  width: calc(
    100% + var(--encroach-by-left) + var(--encroach-by-right)
  );
  margin-left: calc(var(--encroach-by-left) * -1);
  margin-right: calc(var(--encroach-by-right) * -1);
  border-radius: var(--playground-corner-radius);
  background: var(--syntax-bg);
  border: none;
  outline: 1px solid var(--color-gray-200);
  outline-offset: -1px;

  html[data-color-mode='light'] & {
    outline: 1px solid transparent;

    @media ${BREAKPOINTS.smAndSmaller} {
      --playground-corner-radius: 0;
    }
  }

  ${AsideWrapper} & {
    /* We don't need the “content outline” inside an Aside, since there's natural contrast between the background colors. */
    outline: none;

    @media ${BREAKPOINTS.smAndSmaller} {
      /* We don't want to sit on top of the Aside’s left border, so we'll encroach by a few less pixels on the left
      */
      --encroach-by-left: calc(16px - var(--aside-border-width));
      --playground-corner-radius: 0;
    }
  }
`;

export const FullscreenWrapper = styled(Space)`
  position: fixed;
  z-index: 2;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: 100%;
  margin: 0;
  display: flex;
  flex-direction: column;
  background: var(--syntax-bg);
  border: none;
`;

export default CodeWrapper;
